/**
 * Applies the brand primary color to CSS custom properties.
 * Extracted from useBrandTheme to keep hook functions short.
 */
export const applyPrimaryColorToDocument = (primaryColor: string): void => {
  const oklch = hexToOKLCH(primaryColor);
  const foreground = isLightColor(primaryColor) ? 'oklch(0.2178 0 0)' : 'oklch(1 0 0)';

  document.documentElement.style.setProperty('--primary', oklch);
  document.documentElement.style.setProperty('--primary-foreground', foreground);
  document.documentElement.style.setProperty('--ring', oklch);
  document.documentElement.style.setProperty('--sidebar-primary', oklch);
  document.documentElement.style.setProperty('--sidebar-primary-foreground', foreground);
  document.documentElement.style.setProperty('--sidebar-ring', oklch);
  document.documentElement.style.setProperty('--chart-1', oklch);
};

const hexToOKLCH = (hex: string): string => {
  const normalized = hex.replace(/^#/, '');
  const r = parseInt(normalized.substring(0, 2), 16) / 255;
  const g = parseInt(normalized.substring(2, 4), 16) / 255;
  const b = parseInt(normalized.substring(4, 6), 16) / 255;

  const linearize = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const lr = linearize(r);
  const lg = linearize(g);
  const lb = linearize(b);

  const x = 0.4124564 * lr + 0.3575761 * lg + 0.1804375 * lb;
  const y = 0.2126729 * lr + 0.7151522 * lg + 0.0721750 * lb;
  const z = 0.0193339 * lr + 0.1191920 * lg + 0.9503041 * lb;

  const l_ = Math.cbrt(0.8189330101 * x + 0.3618667424 * y - 0.1288597137 * z);
  const m_ = Math.cbrt(0.0329845436 * x + 0.9293118715 * y + 0.0361456387 * z);
  const s_ = Math.cbrt(0.0482003018 * x + 0.2643662691 * y + 0.6338517070 * z);

  const L = 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_;
  const a = 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_;
  const bOk = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_;

  const C = Math.sqrt(a * a + bOk * bOk);
  let H = Math.atan2(bOk, a) * (180 / Math.PI);
  if (H < 0) H += 360;

  return `oklch(${L.toFixed(4)} ${C.toFixed(4)} ${H.toFixed(4)})`;
};

const isLightColor = (hex: string): boolean => {
  const normalized = hex.replace(/^#/, '');
  const r = parseInt(normalized.substring(0, 2), 16) / 255;
  const g = parseInt(normalized.substring(2, 4), 16) / 255;
  const b = parseInt(normalized.substring(4, 6), 16) / 255;

  const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const luminance =
    0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);

  return luminance > 0.5;
};
